import { useMemo } from 'react';
import {
  useArchivedClients,
  useClientTiers,
  type Tier,
} from '@busy/api';
import { TIER_PRIORITY } from '@busy/core';
import { useT } from '../../lib/i18n';
import type { ChipKey } from './AlphaClientList';

// Compact chip-strip filter for the Clients-tab roster. Replaces the
// older full-width ClientTiersTile (Panel + rows + progress bars) —
// same data, same filter behavior, ~5× smaller vertical footprint.
//
// Each chip: small SVG icon + count + label. Sorted by the same
// TIER_PRIORITY order the old tile used (at-risk first because
// it's the most action-relevant), with "All" pinned to the start
// and "Archived" pinned to the end.
//
// Behavior:
//   * Selected chip → filled state (navy / gold), pulls the
//     AlphaClientList below it down to that tier
//   * Unselected → bg-alt outline
//   * Zero count → dimmed but still tappable (lets you confirm
//     "yeah, no VIPs yet"). The old tile auto-disabled these;
//     keeping them tappable here feels less paternalistic given
//     the smaller footprint.
//   * Strip overflows horizontally on narrow screens with a
//     scroll-snap pattern so each chip lands cleanly when paged.

const TIER_ICON: Record<Tier | 'all' | 'archived', IconName> = {
  all: 'users',
  'at-risk': 'alert',
  vip: 'crown',
  regular: 'check',
  new: 'sparkle',
  active: 'bolt',
  archived: 'archive',
};

const TIER_TONE: Record<Tier | 'all' | 'archived', {
  active: string;
  icon: string;
}> = {
  all: {
    active: 'bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900',
    icon: 'text-navy-900 dark:text-gold-400',
  },
  'at-risk': {
    active: 'bg-red-600 text-white',
    icon: 'text-red-600 dark:text-red-400',
  },
  vip: {
    active: 'bg-gold-500 text-navy-900',
    icon: 'text-gold-600 dark:text-gold-400',
  },
  regular: {
    active: 'bg-emerald-600 text-white',
    icon: 'text-emerald-700 dark:text-emerald-400',
  },
  new: {
    active: 'bg-blue-600 text-white',
    icon: 'text-blue-700 dark:text-blue-400',
  },
  active: {
    active: 'bg-ink-700 text-white dark:bg-ink-300 dark:text-ink-900',
    icon: 'text-ink-700 dark:text-ink-400',
  },
  archived: {
    active: 'bg-ink-500 text-white',
    icon: 'text-ink-500',
  },
};

export function ClientTiersChips({
  selected,
  onSelect,
}: {
  selected: ChipKey;
  onSelect: (chip: ChipKey) => void;
}) {
  const t = useT();
  const tiers = useClientTiers();
  const archived = useArchivedClients();

  const tierCounts = useMemo(() => {
    const m: Record<Tier, number> = {
      vip: 0,
      regular: 0,
      new: 0,
      active: 0,
      'at-risk': 0,
    };
    (tiers.data ?? []).forEach((row) => {
      m[row.tier] = (m[row.tier] ?? 0) + 1;
    });
    return m;
  }, [tiers.data]);

  const totalActive = Object.values(tierCounts).reduce((s, n) => s + n, 0);
  const archivedCount = (archived.data ?? []).length;

  // Build the row of chips in priority order. "All" pinned first,
  // tiers in TIER_PRIORITY order (at-risk → vip → regular → new →
  // active), Archived last.
  const chips: Array<{ key: ChipKey; count: number }> = [
    { key: 'all', count: totalActive },
    ...TIER_PRIORITY.map((tier) => ({
      key: tier as ChipKey,
      count: tierCounts[tier],
    })),
    { key: 'archived', count: archivedCount },
  ];

  return (
    <div
      className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 pb-1"
      // Scrollbar hidden on the chip strip — mobile browsers handle
      // overflow scrolling natively without needing a visible bar.
      style={{ scrollbarWidth: 'none' }}
      role="tablist"
      aria-label={t('clients.filter.aria')}
    >
      {chips.map(({ key, count }) => {
        const isSelected = selected === key;
        const isZero = count === 0;
        const tone = TIER_TONE[key];
        const label =
          key === 'all'
            ? t('clients.filter.all')
            : key === 'archived'
              ? t('clients.filter.archived')
              : t(`tier.${key}`);
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={isSelected}
            onClick={() => onSelect(key)}
            className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12px] font-bold transition-all border ${
              isSelected
                ? `${tone.active} border-transparent shadow-sm`
                : 'bg-bg-alt border-line text-ink-700 hover:bg-bg-alt/70'
            } ${isZero && !isSelected ? 'opacity-50' : ''}`}
          >
            <TierIcon
              name={TIER_ICON[key]}
              className={`w-3.5 h-3.5 ${isSelected ? '' : tone.icon}`}
            />
            <span className="tabular-nums">{count}</span>
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ────────── Icons ──────────

type IconName = 'users' | 'alert' | 'crown' | 'check' | 'sparkle' | 'bolt' | 'archive';

function TierIcon({ name, className }: { name: IconName; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {name === 'users' && (
        <>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </>
      )}
      {name === 'alert' && (
        <>
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </>
      )}
      {name === 'crown' && (
        <path d="M2 18l2-9 5 4 3-7 3 7 5-4 2 9H2z" />
      )}
      {name === 'check' && (
        <polyline points="20 6 9 17 4 12" />
      )}
      {name === 'sparkle' && (
        <>
          <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
        </>
      )}
      {name === 'bolt' && (
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      )}
      {name === 'archive' && (
        <>
          <rect x="2" y="3" width="20" height="5" rx="1" />
          <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
          <line x1="10" y1="13" x2="14" y2="13" />
        </>
      )}
    </svg>
  );
}
